import { useEffect, useRef, useState } from "react";
import { Loader2, ExternalLink } from "lucide-react";

export default function JitsiCall({ room, displayName = "Participante", onEnded }) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let script = document.querySelector('script[data-jitsi="1"]');
    const timeout = setTimeout(() => { if (!apiRef.current) setFailed(true); }, 9000);
    const init = () => {
      if (!containerRef.current || apiRef.current || !window.JitsiMeetExternalAPI) return;
      const api = new window.JitsiMeetExternalAPI("meet.jit.si", {
        roomName: room,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: { displayName },
        configOverwrite: { disableDeepLinking: true, prejoinPageEnabled: true, startWithAudioMuted: false },
        interfaceConfigOverwrite: {
          MOBILE_APP_PROMO: false, SHOW_JITSI_WATERMARK: false, SHOW_WATERMARK_FOR_GUESTS: false,
          SHOW_BRAND_WATERMARK: false, SHOW_POWERED_BY: false, JITSI_WATERMARK_LINK: "",
          DEFAULT_LOGO_URL: "", DEFAULT_WELCOME_PAGE_LOGO_URL: "", HIDE_DEEP_LINKING_LOGO: true,
          DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
        },
      });
      apiRef.current = api;
      setLoading(false);
      clearTimeout(timeout);
      api.addListener("videoConferenceJoined", () => setLoading(false));
      api.addListener("readyToClose", () => { onEnded && onEnded(); });
    };

    if (window.JitsiMeetExternalAPI) {
      init();
    } else if (script) {
      script.addEventListener("load", init);
      script.addEventListener("error", () => setFailed(true));
    } else {
      script = document.createElement("script");
      script.src = "https://meet.jit.si/external_api.js";
      script.async = true;
      script.dataset.jitsi = "1";
      script.onload = init;
      script.onerror = () => setFailed(true);
      document.body.appendChild(script);
    }

    return () => {
      clearTimeout(timeout);
      if (apiRef.current) {
        try { apiRef.current.dispose(); } catch (e) {}
        apiRef.current = null;
      }
    };
  }, [room, displayName, onEnded]);

  return (
    <div className="relative w-full h-full bg-black" data-testid="jitsi-container">
      {failed ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-4 px-6 text-center">
          <p className="text-sm text-white/80 max-w-md">
            Não foi possível carregar a sala de vídeo embutida. Abra a sala diretamente no navegador:
          </p>
          <a href={`https://meet.jit.si/${room}`} target="_blank" rel="noopener noreferrer"
            data-testid="jitsi-fallback-link"
            className="inline-flex items-center gap-2 bg-white text-black rounded-full px-5 py-2.5 text-sm font-medium hover:bg-white/90 transition-colors">
            <ExternalLink className="w-4 h-4" /> Abrir sala de vídeo
          </a>
        </div>
      ) : loading ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-3">
          <Loader2 className="w-6 h-6 animate-spin" />
          <p className="text-sm text-white/70">A ligar à sala de vídeo…</p>
        </div>
      ) : null}
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}
